// ---------------------------------------------------------------------------
// severity.js — tokens canonicos de severidade do Inputs V3.
// ---------------------------------------------------------------------------
//
// Por que este arquivo existe:
//   A semantica de severidade do pipeline V3 (sev=1 informativo, sev=2
//   atencao, sev=3 bloqueante) aparecia replicada em pelo menos 3
//   lugares: ``NowPane.js`` (timeline), ``InputBoxV3.js`` (donut +
//   tooltip) e ``CrossLinksPane.js`` (caixas). Cada arquivo tinha
//   seu proprio mapa de severidade -> icone / label / cor /
//   ``sevKind``. Quando o time decidiu (rev. 2026-04-27) padronizar
//   sev=1 como ``note``/azul (e nao ``ok``/verde), houve dessincronia
//   por dias entre os arquivos.
//
//   Centralizar aqui faz tres coisas:
//     1. Nome unico para o ``sevKind`` (string usada para classes CSS
//        do drawer ``tlItem*``, ``tlCrossCard*``, etc.).
//     2. Mapeamento explicito ``severity:int -> sevKind:string``.
//     3. Lista canonica de grupos (``SEVERITY_GROUPS``) usada como
//        cabecalho no agrupamento de sub-cards na timeline e em
//        outras visoes.
//
//   ATENCAO: as cores reais dos sub-cards continuam vivendo no
//   ``styles.js`` (classes ``tlCrossCardErr``, ``tlSeverityGroupLabelErr``
//   etc.) — nao tentamos centralizar a paleta porque ela depende de
//   tokens de tema (``dangerColor`` / ``warningColor`` / ``infoColor``)
//   que cada componente injeta via ``makeStyles``. Aqui ficam apenas
//   os identificadores semanticos.
//
// Como consumir:
//   import { SEVERITY_KIND, SEVERITY_GROUPS, sevKindFor } from
//     "../../severity";
//
//   const sk = SEVERITY_KIND[note.severity];
//   const sevKind = sevKindFor(note.severity); // "note" | "warn" | "err"
// ---------------------------------------------------------------------------

import InfoOutlined from "@material-ui/icons/InfoOutlined";
import Warning from "@material-ui/icons/Warning";
import Error from "@material-ui/icons/Error";

// Tabela completa de severidades reconhecidas pelo executor V3.
//
// Chaves sao integers para casar com o campo ``severity`` da
// ``InputCheckNoteV3``. Cada entrada traz:
//   - ``kind``: o ``sevKind`` consumido pelas classes CSS
//     (``tlItemNote`` / ``tlItemWarn`` / ``tlItemErr``,
//     ``tlCrossCardNote`` / etc.). NUNCA renomear sem fazer a
//     varredura nos styles.
//   - ``Icon``: componente de icone Material-UI usado no item-mae da
//     timeline e no cabecalho de grupos.
//   - ``label``: rotulo curto (singular) — usado quando precisamos
//     citar a severidade de UMA nota (ex.: tooltip, sub-card).
// ``labelKey`` aponta para uma chave i18n (consumida via ``t()`` no
// callsite). Mantemos o nome do campo curto porque ele aparece em
// muitos componentes; quem renderiza chama ``t(sk.labelKey)``.
export const SEVERITY_KIND = {
  1: {
    kind: "note",
    Icon: InfoOutlined,
    labelKey: "modeling_inputs_v3.severity.kind_observation",
  },
  2: {
    kind: "warn",
    Icon: Warning,
    labelKey: "modeling_inputs_v3.severity.kind_attention",
  },
  3: {
    kind: "err",
    Icon: Error,
    labelKey: "modeling_inputs_v3.severity.kind_error_structure",
  },
};

// Helper: ``severity`` (int) -> ``sevKind`` (string). Tolera valores
// fora do dominio (null, NaN, 0): qualquer coisa <=1 vira ``note``.
// Util para callsites que recebem ``note.severity`` direto do
// backend e nao querem se preocupar com defensividade.
export function sevKindFor(severity) {
  const n = Number(severity || 0);
  if (n >= 3) return "err";
  if (n === 2) return "warn";
  return "note";
}

// Ordem e rotulos dos GRUPOS de severidade renderizados como
// separadores antes das caixas (sub-cards) dentro de um item de
// timeline. Sequencia segue a leitura natural: informativo (mais
// leve) -> atencao -> bloqueante.
//
// Os labels sao identicos para notas STR (estrutura) e CSL
// (cross-linking) por design — semantica comum (ver
// ``08_severity_rules.md``).
//
// ``labelClsKey`` aponta para a classe em ``drawer/styles.js``
// (objeto retornado por ``makeStyles``). Mantemos como string para
// nao acoplar este arquivo ao stylesheet — quem renderiza faz
// ``classes[g.labelClsKey]``.
export const SEVERITY_GROUPS = [
  {
    sevKind: "note",
    labelKey: "modeling_inputs_v3.severity.group_low",
    labelClsKey: "tlSeverityGroupLabelNote",
  },
  {
    sevKind: "warn",
    labelKey: "modeling_inputs_v3.severity.group_attention",
    labelClsKey: "tlSeverityGroupLabelWarn",
  },
  {
    sevKind: "err",
    labelKey: "modeling_inputs_v3.severity.group_critical",
    labelClsKey: "tlSeverityGroupLabelErr",
  },
];
